import React from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import FacilityTypeForm from "pages/FacilityType/Form";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {FacilityTypeData} from "types/FacilityType";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import NotAuthorised from "../../NotAuthorised";
import {useAuth} from "../../../context/AuthContext";

function MutateFacilityType() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: facilityType, isLoading} = useQuery(
    ['facility-types', {id}],
    () => ngage.facilityTypes.get(Number(id)),
    {enabled: !!id && auth.can('facility-types:read')}
  )

  const store = useMutation((data: FacilityTypeData) => ngage.facilityTypes.store(data))
  const update = useMutation((data: FacilityTypeData) => ngage.facilityTypes.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: FacilityTypeData, {setSubmitting}: FormikHelpers<FacilityTypeData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('facility-types.all')

    setSubmitting(false)
    navigate('/facility-types')
  }

  // Handle store mutation
  async function handleStore(values: FacilityTypeData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Facility Type')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Facility Type')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: FacilityTypeData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Facility Type')} Updated`, `${values.name} has been updated`)
      await invalidate(['facility-types', {id}])
    } catch (error) {
      notify("error", `${termFor('Facility Type')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutating
  if (auth.cannot('facility-types:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Facility Types')]: '/facility-types',
    [`New ${termFor('Facility Type')}`]: '/facility-types/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Facility Types')]: '/facility-types',
    [`Edit ${termFor('Facility Type')}`]: `/facility-types/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Facility Type')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <FacilityTypeForm facilityType={facilityType} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateFacilityType;